/**
 * Cálculos para o hero de alocação (Layer 1 do mockup Orçamento).
 */

export const ALLOCATION_EPSILON = 0.01;

export function sumAllocatedFromGroups(groups = []) {
  return groups.reduce((sum, group) => {
    const value = Number(group.categories_budget_sum_eur ?? group.budget_eur ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
}

/**
 * @param {{ groups?: Array<object>, cycleIncomeEur?: number|string|null }} params
 */
export function buildAllocationSegments({ groups = [], cycleIncomeEur }) {
  const income = Number(cycleIncomeEur ?? 0);
  if (!Number.isFinite(income) || income <= 0) {
    return {
      segments: [],
      unassignedPct: 100,
      overPct: 0,
      isOverAllocated: false,
      totalAssigned: 0,
      income: 0,
    };
  }

  const totalAssigned = sumAllocatedFromGroups(groups);
  const isOverAllocated = totalAssigned > income + ALLOCATION_EPSILON;

  if (isOverAllocated) {
    const incomeSharePct = (income / totalAssigned) * 100;
    const overPct = Math.max(0, 100 - incomeSharePct);
    const segments = groups
      .map((group) => {
        const amount = Number(group.categories_budget_sum_eur ?? group.budget_eur ?? 0);
        if (!Number.isFinite(amount) || amount <= 0) return null;
        return {
          id: group.id,
          name: group.name,
          color: group.color || '#7C9E6F',
          amount,
          widthPct: (amount / totalAssigned) * incomeSharePct,
        };
      })
      .filter(Boolean);

    return {
      segments,
      unassignedPct: 0,
      overPct,
      isOverAllocated: true,
      totalAssigned,
      income,
    };
  }

  const segments = groups
    .map((group) => {
      const amount = Number(group.categories_budget_sum_eur ?? group.budget_eur ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) return null;
      return {
        id: group.id,
        name: group.name,
        color: group.color || '#7C9E6F',
        amount,
        widthPct: Math.min(100, (amount / income) * 100),
      };
    })
    .filter(Boolean);

  const assignedPct = Math.min(100, (totalAssigned / income) * 100);
  const unassignedPct = Math.max(0, 100 - assignedPct);

  return {
    segments,
    unassignedPct,
    overPct: 0,
    isOverAllocated: false,
    totalAssigned,
    income,
  };
}

/**
 * @param {{ totalAssigned: number, income: number }} params
 * @returns {{ state: 'unknown'|'under'|'complete'|'over', remaining: number|null, excess: number|null, isComplete: boolean }}
 */
export function allocationCompletionHint({ totalAssigned, income }) {
  if (!Number.isFinite(income) || income <= 0) {
    return { state: 'unknown', remaining: null, excess: null, isComplete: false };
  }

  if (totalAssigned > income + ALLOCATION_EPSILON) {
    return {
      state: 'over',
      remaining: null,
      excess: totalAssigned - income,
      isComplete: false,
    };
  }

  const remaining = income - totalAssigned;
  if (remaining <= ALLOCATION_EPSILON) {
    return { state: 'complete', remaining: 0, excess: null, isComplete: true };
  }

  return { state: 'under', remaining, excess: null, isComplete: false };
}

/**
 * Percentagem «com destino» para o anel (pode ultrapassar 100% se over).
 * @param {{ totalAssigned: number, income: number }} params
 */
export function allocationAssignedPct({ totalAssigned, income }) {
  if (!Number.isFinite(income) || income <= 0) return 0;
  if (!Number.isFinite(totalAssigned) || totalAssigned <= 0) return 0;
  return Math.round((totalAssigned / income) * 100);
}
